(() => {
  if (window.__himpHomepageReady) return;
  window.__himpHomepageReady = true;
  const initialiseHero = (hero) => {
    if (hero.dataset.ready === 'true') return;
    hero.dataset.ready = 'true';
    const track = hero.querySelector('[data-hero-track]');
    const viewport = hero.querySelector('[data-hero-viewport]');
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    const tabs = [...hero.querySelectorAll('[data-hero-tab]')];
    if (!track || !viewport || !slides.length) return;

    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer;
    let startX = 0;
    let deltaX = 0;
    let dragging = false;
    let suppressClick = false;

    const show = (nextIndex, userInitiated = false) => {
      index = (nextIndex + slides.length) % slides.length;
      track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (userInitiated) restart();
    };

    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      if (hero.dataset.autoplay === 'true' && slides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        timer = window.setInterval(() => show(index + 1), Number(hero.dataset.interval) || 6000);
      }
    };
    const restart = () => start();

    tabs.forEach((tab, tabIndex) => tab.addEventListener('click', (event) => {
      if (tabIndex !== index) {
        event.preventDefault();
        show(tabIndex, true);
      }
    }));
    hero.querySelector('[data-hero-previous]')?.addEventListener('click', () => show(index - 1, true));
    hero.querySelector('[data-hero-next]')?.addEventListener('click', () => show(index + 1, true));
    viewport.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      dragging = true;
      suppressClick = false;
      startX = event.clientX;
      deltaX = 0;
      stop();
    });
    viewport.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      deltaX = event.clientX - startX;
      if (Math.abs(deltaX) > 8 && !suppressClick) {
        suppressClick = true;
        viewport.setPointerCapture(event.pointerId);
        track.classList.add('is-dragging');
      }
      if (!suppressClick) return;
      track.style.transform = `translate3d(calc(-${index * 100}% + ${deltaX}px), 0, 0)`;
    });
    const finishDrag = () => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      if (Math.abs(deltaX) > Math.min(100, viewport.clientWidth * 0.12)) show(index + (deltaX < 0 ? 1 : -1), true);
      else show(index, true);
    };
    viewport.addEventListener('pointerup', finishDrag);
    viewport.addEventListener('pointercancel', finishDrag);
    viewport.addEventListener('dragstart', (event) => event.preventDefault());
    viewport.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, true);
    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);
    hero.addEventListener('focusin', stop);
    hero.addEventListener('focusout', start);
    show(index);
    start();
  };

  const initialiseProducts = (switcher) => {
    if (switcher.dataset.ready === 'true') return;
    switcher.dataset.ready = 'true';
    const tabs = [...switcher.querySelectorAll('[data-product-tab]')];
    const panels = [...switcher.querySelectorAll('[data-product-panel]')];
    const viewAll = switcher.querySelector('[data-product-view-all]');
    if (!tabs.length || !panels.length) return;

    let sectionResponse;
    const loadPanel = async (panel) => {
      if (panel.dataset.productsLoaded === 'true' || panel.dataset.productsLoading === 'true') return;
      panel.dataset.productsLoading = 'true';
      panel.setAttribute('aria-busy', 'true');
      try {
        if (!sectionResponse) {
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', switcher.dataset.sectionId);
          const controller = new AbortController();
          const timeout = window.setTimeout(() => controller.abort(), 15000);
          sectionResponse = fetch(url, { credentials: 'same-origin', signal: controller.signal }).then(async response => {
            if (!response.ok) throw new Error('Product tabs could not load');
            return new DOMParser().parseFromString(await response.text(), 'text/html');
          }).finally(() => window.clearTimeout(timeout));
        }
        const html = await sectionResponse;
        const fetchedPanel = html.querySelector('[data-product-panel][data-index="' + panel.dataset.index + '"]');
        if (!fetchedPanel || fetchedPanel.dataset.productsLoaded !== 'true') throw new Error('Product tab is unavailable');
        if (!switcher.isConnected) return;
        panel.querySelector('.redesign-catalog__grid').innerHTML = fetchedPanel.querySelector('.redesign-catalog__grid').innerHTML;
        panel.dataset.productsLoaded = 'true';
        initialiseReveals(panel);
      } catch (error) {
        sectionResponse = null;
        panel.querySelector('.redesign-catalog__grid').innerHTML = '<p class="redesign-catalog__empty"><span data-products-error></span> <button type="button" data-products-retry></button> <a data-products-fallback></a></p>';
        panel.querySelector('[data-products-error]').textContent = switcher.dataset.productsError;
        panel.querySelector('[data-products-retry]').textContent = switcher.dataset.productsRetryLabel;
        panel.querySelector('[data-products-fallback]').textContent = switcher.dataset.productsFallbackLabel;
        panel.querySelector('[data-products-fallback]').href = panel.dataset.viewAll;
        panel.querySelector('[data-products-retry]').addEventListener('click', () => loadPanel(panel));
      } finally {
        panel.dataset.productsLoading = 'false';
        panel.removeAttribute('aria-busy');
      }
    };

    const show = (index) => {
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        if (active) {
          loadPanel(panel);
          panel.classList.remove('is-entering');
          window.requestAnimationFrame(() => panel.classList.add('is-entering'));
        }
        if (active && viewAll) viewAll.href = panel.dataset.viewAll;
      });
    };
    tabs.forEach((tab, tabIndex) => tab.addEventListener('click', () => show(tabIndex)));
    show(Math.max(0, tabs.findIndex((tab) => tab.classList.contains('is-active'))));
  };

  const initialiseReveals = (root = document) => {
    const targets = root.querySelectorAll('.redesign-pathways__title, .redesign-pathways__subtitle, .redesign-pathway, .himp-home__heading, .redesign-brand, .redesign-catalog__tabs, .redesign-product, .redesign-benefit, .redesign-info__values, .redesign-info__card');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    }), { threshold: 0.12, rootMargin: '0px 0px -45px' });
    targets.forEach((target, index) => {
      if (target.dataset.revealReady === 'true') return;
      target.dataset.revealReady = 'true';
      target.classList.add('himp-reveal');
      target.style.setProperty('--reveal-delay', `${(index % 6) * 55}ms`);
      observer.observe(target);
    });
  };

  const initialiseInformationPanel = (panel) => {
    if (panel.dataset.heightReady === 'true') return;
    panel.dataset.heightReady = 'true';
    const values = panel.querySelector('.redesign-info__values');
    const side = panel.querySelector('.redesign-info__side');
    if (!values || !side) return;
    panel.querySelectorAll('details').forEach((details) => { details.open = false; });
    let measureFrame = 0;
    const measure = () => {
      measureFrame = 0;
      if (!panel.isConnected) return;
      const height = `${Math.ceil(values.getBoundingClientRect().height)}px`;
      if (side.style.getPropertyValue('--info-closed-height') !== height) side.style.setProperty('--info-closed-height', height);
    };
    const scheduleMeasure = () => {
      if (!measureFrame) measureFrame = window.requestAnimationFrame(measure);
    };
    scheduleMeasure();
    if (document.fonts?.ready) document.fonts.ready.then(scheduleMeasure);
    window.addEventListener('resize', () => {
      if (![...panel.querySelectorAll('details')].some((details) => details.open)) scheduleMeasure();
    }, { passive: true });
  };

  const initialise = (root = document) => {
    root.querySelectorAll('[data-home-hero]').forEach(initialiseHero);
    root.querySelectorAll('[data-product-switcher]').forEach(initialiseProducts);
    root.querySelectorAll('.redesign-info').forEach(initialiseInformationPanel);
    initialiseReveals(root);
  };
  document.addEventListener('DOMContentLoaded', () => initialise());
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
